import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Link2, Pencil } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ResumeEditor from '../components/ResumeEditor';
import CredlyBadges from '../components/CredlyBadges';
import PortfolioUpload from '../components/PortfolioUpload';
import ProfileAutoSave from '../components/ProfileAutoSave';
import resumeAPI, { generatePublicLinkId } from '../services/api';
import { DEFAULT_RESUME } from '../config/constants';

const ProfileEditPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(DEFAULT_RESUME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [credlyBadges, setCredlyBadges] = useState([]);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [googleSiteUrl, setGoogleSiteUrl] = useState('');
  const [publicUrlSlug, setPublicUrlSlug] = useState('');
  const [publicUrlDraft, setPublicUrlDraft] = useState('');
  const [publicUrlEditing, setPublicUrlEditing] = useState(false);
  const [publicUrlSaving, setPublicUrlSaving] = useState(false);
  const [publicUrlError, setPublicUrlError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadProfile();
    loadPublicProfileSettings();
  }, [isAuthenticated, navigate]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const resumes = await resumeAPI.fetchAll({ full: true });
      const primaryResume = resumes.find(r => r.is_primary) || resumes[0];
      if (primaryResume) {
        loadResumeData(primaryResume);
      } else {
        setProfile({
          ...DEFAULT_RESUME,
          training: [],
          title: user?.name || user?.email || 'My Profile',
          contact_email: user?.email || '',
          is_primary: true,
          pronouns: '',
          credentials: ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setProfile({
        ...DEFAULT_RESUME,
        title: user?.name || user?.email || 'My Profile',
        contact_email: user?.email || '',
        is_primary: true,
        pronouns: '',
        credentials: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPublicProfileSettings = async () => {
    try {
      const profileData = await resumeAPI.getMyProfile?.();
      const slug = profileData?.profile_slug || profileData?.profileSlug || '';
      setPublicUrlSlug(slug);
      setPublicUrlDraft(slug);
    } catch (e) {
      console.warn('Failed to load public profile settings:', e);
    }
  };

  const loadResumeData = (resume) => {
    const loadedProfile = {
      ...DEFAULT_RESUME,
      ...resume,
      summary: resume.summary ?? '',
      professional_summary: resume.professional_summary ?? '',
      photo_url: resume.photo_url ?? '',
      banner_url: resume.banner_url ?? '',
      pronouns: resume.pronouns ?? '',
      credentials: resume.credentials ?? '',
      profile_frame: resume.profile_frame ?? '',
      headline: resume.headline ?? '',
      target_role: resume.target_role ?? '',
      experience: Array.isArray(resume.experience) ? resume.experience : [],
      education: Array.isArray(resume.education) ? resume.education : [],
      training: Array.isArray(resume.training) ? resume.training : [],
      skills: Array.isArray(resume.skills) ? resume.skills : [],
      projects: Array.isArray(resume.projects) ? resume.projects : [],
      certifications: Array.isArray(resume.certifications) ? resume.certifications : [],
      languages: Array.isArray(resume.languages) ? resume.languages : []
    };
    setProfile(loadedProfile);
    setCredlyBadges(resume.credly_badges || []);
    setPortfolioFiles(resume.portfolio_files || []);
    setGoogleSiteUrl(resume.google_site_url || '');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const profileData = {
        ...profile,
        is_primary: true,
        public_link_id: profile.public_link_id || generatePublicLinkId(),
        credly_badges: credlyBadges,
        portfolio_files: portfolioFiles,
        google_site_url: googleSiteUrl,
        headline: profile.headline ?? '',
        pronouns: profile.pronouns ?? '',
        credentials: profile.credentials ?? '',
        professional_summary: profile.professional_summary ?? '',
        profile_frame: profile.profile_frame ?? '',
        experience: Array.isArray(profile.experience) ? profile.experience : [],
        education: Array.isArray(profile.education) ? profile.education : [],
        training: Array.isArray(profile.training) ? profile.training : [],
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        projects: Array.isArray(profile.projects) ? profile.projects : [],
        certifications: Array.isArray(profile.certifications) ? profile.certifications : [],
        languages: Array.isArray(profile.languages) ? profile.languages : []
      };

      if (profile.id || profile._id) {
        await resumeAPI.update(profile.id || profile._id, profileData);
        showMessage('Profile updated successfully!', 'success');
      } else {
        const newProfile = await resumeAPI.create(profileData);
        showMessage('Profile created successfully!', 'success');
        setProfile({ ...profileData, id: newProfile.id || newProfile._id });
      }
      setTimeout(() => navigate('/profile'), 900);
    } catch (error) {
      showMessage('Failed to save profile', 'error');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const sanitizeSlugInput = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const validateSlug = (value) => {
    const slug = String(value || '').trim();
    if (slug.length < 3 || slug.length > 100) {
      return 'Custom URL must be 3-100 characters.';
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      return 'Use only letters, numbers, or hyphens.';
    }
    return '';
  };

  const handleSaveCustomUrl = async () => {
    const sanitized = sanitizeSlugInput(publicUrlDraft);
    const errorText = validateSlug(sanitized);
    if (errorText) {
      setPublicUrlError(errorText);
      return;
    }
    setPublicUrlSaving(true);
    setPublicUrlError('');
    try {
      const updated = await resumeAPI.updateMyProfile?.({ profile_slug: sanitized });
      const slug = updated?.profile_slug || sanitized;
      setPublicUrlSlug(slug);
      setPublicUrlDraft(slug);
      setPublicUrlEditing(false);
      showMessage('Custom URL updated', 'success');
    } catch (e) {
      setPublicUrlError(e?.message || 'Failed to update URL');
    } finally {
      setPublicUrlSaving(false);
    }
  };

  const updateProfile = useCallback((field, value) => {
    setProfile(prev => {
      // Ensure arrays are always arrays
      let processedValue = value;
      if (['experience', 'education', 'training', 'skills', 'projects', 'certifications', 'languages'].includes(field)) {
        processedValue = Array.isArray(value) ? value : [];
      }
      
      const updated = { ...prev, [field]: processedValue };
      
      // Debug log for pronouns, credentials, professional_summary, experience, and training
      if (field === 'pronouns' || field === 'credentials' || field === 'professional_summary' || field === 'experience' || field === 'training') {
        console.log(`Updating ${field}:`, processedValue, 'Previous:', prev[field], 'New value:', processedValue);
        if (field === 'experience' || field === 'training') {
          console.log(`${field} array length:`, Array.isArray(processedValue) ? processedValue.length : 'not an array');
          console.log(`${field} items:`, processedValue);
        }
      }
      return updated;
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        {/* Message Banner */}
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Your Professional Networking Profile</h1>
              <p className="text-gray-600 mt-1">
                Build your LinkedIn-style networking profile
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate('/profile')}
                className="btn btn-outline inline-flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary inline-flex items-center space-x-2"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Saving...' : 'Save Profile'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Public profile & URL */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Public profile & URL</h2>
              <p className="text-sm text-gray-600 mt-1">
                Personalize the URL for your profile.
              </p>
              <div className="mt-3 text-sm text-gray-700 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gray-500" />
                <span className="break-all">
                  {window.location.origin}/in/{publicUrlSlug || 'your-name'}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setPublicUrlDraft(publicUrlSlug || '');
                setPublicUrlEditing(true);
                setPublicUrlError('');
              }}
              className="btn btn-outline inline-flex items-center gap-2"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
          </div>

          {publicUrlEditing && (
            <div className="mt-4 border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Edit your custom URL
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Your custom URL must contain 3-100 letters or numbers. No spaces or special characters.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {window.location.origin}/in/
                </span>
                <input
                  type="text"
                  value={publicUrlDraft}
                  onChange={(e) => {
                    setPublicUrlDraft(e.target.value);
                    setPublicUrlError('');
                  }}
                  className="input flex-1"
                  placeholder="your-name"
                />
              </div>
              {publicUrlError && (
                <p className="text-xs text-red-600 mt-2">{publicUrlError}</p>
              )}
              <div className="flex items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleSaveCustomUrl}
                  disabled={publicUrlSaving}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {publicUrlSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPublicUrlEditing(false);
                    setPublicUrlDraft(publicUrlSlug || '');
                    setPublicUrlError('');
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* AUTO-SAVE & BACKUP COMPONENT (Features A, B, C) */}
        <ProfileAutoSave 
          profileData={profile}
          onRestore={(restoredProfile) => {
            setProfile(restoredProfile);
            setMessage({ type: 'success', text: 'Profile restored successfully!' });
            setTimeout(() => setMessage(null), 3000);
          }}
        />

        {/* Editor */}
        <div className="space-y-6">
          {/* Profile Picture & Background Section - Hidden, use ProfilePage modal instead */}

          <div className="bg-white rounded-lg shadow-md p-6">
            <ResumeEditor resume={profile} updateResume={updateProfile} />
          </div>

          {/* Credly Badges Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <CredlyBadges badges={credlyBadges} onBadgesUpdate={setCredlyBadges} />
          </div>

        </div>

        {/* Bottom Save Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 shadow-lg mt-8 py-4 px-6 rounded-lg">
          <div className="flex justify-end gap-3">
            <button
              onClick={() => navigate('/profile')}
              className="btn btn-outline inline-flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary inline-flex items-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Profile'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditPage;
